import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'../components/ui/accordion';
import {
  Eye,
  Shield,
  Target,
  Calendar,
  BookOpen,
  Phone,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  ChevronRight,
  DollarSign,
  TrendingDown,
  Award,
  Loader2,
  ClipboardCheck } from
'lucide-react';
import { features, tactics } from '../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Popup states
  const [showConsultationPopup, setShowConsultationPopup] = useState(false);
  const [showEbookPopup, setShowEbookPopup] = useState(false);

  // Dynamic content from backend
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [stats, setStats] = useState({ avg_savings: '$4,200', total_customers: '15,000+', success_rate: '97%' });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch dynamic content on mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [testimonialsRes, faqsRes, statsRes] = await Promise.all([
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/faqs`),
        axios.get(`${API}/stats`)]
        );

        setTestimonials(testimonialsRes.data);
        setFaqs(faqsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching content:', error);
        toast.error('Failed to load some content');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Automatic popup timers
  useEffect(() => {
    // Show consultation popup after 30 seconds
    const consultationTimer = setTimeout(() => {
      setShowConsultationPopup(true);
    }, 30000);

    // Show eBook popup after 60 seconds
    const ebookTimer = setTimeout(() => {
      setShowEbookPopup(true);
    }, 60000);

    return () => {
      clearTimeout(consultationTimer);
      clearTimeout(ebookTimer);
    };
  }, []);

  const handleCalendarSignup = async (e) => {
    e.preventDefault();
    if (!calendarEmail) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/signup/email`, {
        email: calendarEmail,
        source: 'calendar'
      });
      toast.success(response.data.message);
      setCalendarEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsultationBooking = () => {
    navigate('/consultation');
  };

  const handleEbookAccess = async () => {
    const email = window.prompt('Enter your email to purchase the eBook ($24.99):');
    if (!email) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payment/ebook/checkout`, {
        email: email,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment. Please try again.');
    }
  };

  const handleChecklistAccess = async () => {
    const email = window.prompt('Enter your email to get the Dealership Checklist:');
    if (!email) return;

    try {
      const response = await axios.post(`${API}/ebook/access`, {
        email: email
      });
      toast.success('Checklist will be sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send checklist. Please try again.');
    }
  };

  const handleHeroSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/signup/email`, {
        email: email,
        source: 'hero'
      });
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const iconMap = {
    eye: Eye,
    shield: Shield,
    target: Target
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_less-pay-secrets/artifacts/zvvw9e4b_IMG_9217.png"
                alt="The Auto Shark Logo"
                className="h-14 w-auto" />

              <span className="text-2xl font-bold"><span className="text-orange-600">The</span><span className="text-gray-900">Auto</span><span className="text-orange-600">Shark</span></span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-orange-600 transition-colors">Features</a>
              <a href="#tactics" className="text-gray-700 hover:text-orange-600 transition-colors">Tactics</a>
              <a href="#resources" className="text-gray-700 hover:text-orange-600 transition-colors">Resources</a>
              <a href="#faq" className="text-gray-700 hover:text-orange-600 transition-colors">FAQ</a>
              <Button onClick={handleEbookAccess} className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold shadow-lg">
                Get The eBook
              </Button>
              <Button onClick={handleConsultationBooking} className="bg-orange-600 hover:bg-orange-700">
                Book Consultation
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700">

              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen &&
          <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <a href="#features" className="text-gray-700 hover:text-orange-600 transition-colors">Features</a>
                <a href="#tactics" className="text-gray-700 hover:text-orange-600 transition-colors">Tactics</a>
                <a href="#resources" className="text-gray-700 hover:text-orange-600 transition-colors">Resources</a>
                <a href="#faq" className="text-gray-700 hover:text-orange-600 transition-colors">FAQ</a>
                <Button onClick={handleEbookAccess} className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold w-full">
                  Get The eBook
                </Button>
                <Button onClick={handleConsultationBooking} className="bg-orange-600 hover:bg-orange-700 w-full">
                  Book Consultation
                </Button>
              </nav>
            </div>
          }
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1692406069831-0bb7ea297645"
            alt="Car dealership"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl py-12 sm:py-20">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Stop Overpaying at Dealerships. <span className="text-orange-500">Start Winning.</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed">
              Navigate car purchases with <strong>clarity</strong>, <strong>confidence</strong>, and <strong>strategy</strong>—protecting you from overpaying on price, rates, and fees.
            </p>
            
            <form onSubmit={handleHeroSignup} className="flex flex-col sm:flex-row gap-3 mb-6">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-14 text-lg bg-white/95 border-2 border-white"
                disabled={isSubmitting} />

              <Button
                type="submit"
                className="h-14 px-8 text-lg bg-orange-600 hover:bg-orange-700"
                disabled={isSubmitting}>

                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Sending...
                  </> :

                <>
                    Get Free Calendar <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                }
              </Button>
            </form>
            
            <p className="text-sm text-gray-300">
              Join 15,000+ smart car buyers who refuse to get ripped off.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-orange-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-white mb-2">{stats.avg_savings}</div>
              <div className="text-white/90 text-lg">Average Customer Savings</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-white mb-2">{stats.total_customers}</div>
              <div className="text-white/90 text-lg">Smart Buyers Empowered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-white mb-2">{stats.success_rate}</div>
              <div className="text-white/90 text-lg">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* eBook CTA Banner Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 via-orange-600 to-red-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-yellow-400 text-gray-900 px-4 py-2 rounded-full font-bold text-sm mb-4 animate-pulse">
              🔥 MOST POPULAR - LIMITED TIME OFFER
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">
              Get "The Dealership Killer" eBook
            </h2>
            <p className="text-xl sm:text-2xl mb-6 text-white/95">
              The complete insider playbook that's already helped <strong>15,000+ buyers save an average of $4,800</strong> per car purchase
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-yellow-400" />
                <span className="text-lg">Every negotiation tactic</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-yellow-400" />
                <span className="text-lg">Dealership secrets exposed</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-yellow-400" />
                <span className="text-lg">Step-by-step scripts</span>
              </div>
            </div>
            <Button
              onClick={handleEbookAccess}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-extrabold text-xl px-12 py-7 h-auto shadow-2xl transform hover:scale-105 transition-all">

              <BookOpen className="mr-3 w-6 h-6" />
              Get Instant Access Now
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
            <p className="text-sm text-white/80 mt-4">
              Join thousands of smart buyers who refuse to overpay
            </p>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              The Auto Shark Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We arm you with insider knowledge that levels the playing field
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Secret Tactics Section */}
      <section id="tactics" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Secret Tactics That <span className="text-orange-600">Actually Work</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These aren't generic tips—they're battle-tested strategies from dealership insiders who've flipped the script
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {tactics.map((tactic) =>
            <div
              key={tactic.id}
              className="group p-8 bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl hover:border-orange-500 transition-all hover:shadow-lg">

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tactic.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{tactic.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Resources/CTAs Section */}
      <section id="resources" className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Arm Yourself with the Right Resources
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Choose your path to smarter car buying
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Calendar CTA */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10 hover:border-orange-500 transition-all hover:scale-105">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h3 className="uppercase !font-extrabold !text-2xl !text-center mb-3 text-yellow-400">Free Calendar</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Get the "Best Time to Buy Calendar" and know exactly when dealerships are most vulnerable to negotiation.
                </p>
                <form onSubmit={handleCalendarSignup} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={calendarEmail}
                    onChange={(e) => setCalendarEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    disabled={isSubmitting} />

                  <Button
                    type="submit"
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={isSubmitting}>

                    {isSubmitting ?
                    <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Sending...
                      </> :

                    'Send Me the Calendar'
                    }
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Consultation CTA */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-orange-500 hover:scale-105 transition-all">
              <CardContent className="p-8">
                <div className="absolute top-4 right-4">
                  <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="uppercase !font-extrabold !text-xl !text-center mb-3 text-yellow-400">1-on-1 Consultation</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Get personalized advice tailored to your exact situation. We'll create a custom strategy for your next purchase.
                </p>
                <Button onClick={handleConsultationBooking} className="w-full bg-orange-600 hover:bg-orange-700">
                  Book Your Call <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* eBook CTA */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10 hover:border-orange-500 transition-all hover:scale-105">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h3 className="uppercase !font-extrabold !text-2xl !text-center mb-3 text-yellow-400">The Dealership Killer eBook</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  "How Smart Car Buyers Pay Less Everytime" - The insider secrets every dealership prays you never discover.
                </p>
                <Button onClick={handleEbookAccess} className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 w-full hover:bg-gray-100 !font-medium !text-sm gap-2 px-4 py-2 rounded-md !shadow-sm !text-[#FFFFFF] !bg-[#C2410C]">
                  Access eBook <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Checklist CTA */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10 hover:border-orange-500 transition-all hover:scale-105">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                  <ClipboardCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="uppercase !font-extrabold !text-2xl !text-center mb-3 text-yellow-400">Dealership Checklist</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Get the ultimate pre-purchase checklist that ensures you never miss a critical negotiation point or hidden fee.
                </p>
                <Button onClick={handleChecklistAccess} className="w-full bg-orange-600 hover:bg-orange-700">
                  Get Checklist <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Real Savings from Real People
            </h2>
            <p className="text-xl text-gray-600">See what Auto Shark customers are saying</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="border-2 hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full mr-4" />

                    <div>
                      <div className="font-bold text-lg text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}</div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center text-orange-600 font-bold text-lg">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Saved ${testimonial.savings.toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">Everything you need to know about Auto Shark</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) =>
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="bg-white border-2 border-gray-200 rounded-lg px-6">

                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-orange-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Stop Getting Ripped Off?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of empowered car buyers who walk into dealerships with confidence—and walk out with massive savings.
          </p>
          <Button
            onClick={handleConsultationBooking}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto">

            Book Your Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container sm:px-6 lg:px-8 mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="inline-block !mb-[16px] !pt-[16px] !pb-[16px] !pl-[18px] !pr-[65px] rounded-lg bg-white">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://customer-assets.emergentagent.com/job_less-pay-secrets/artifacts/zvvw9e4b_IMG_9217.png"
                    alt="The Auto Shark Logo"
                    className="h-14 w-auto" />

                  <span className="text-xl font-bold"><span className="text-orange-600">The</span><span className="text-gray-900">Auto</span><span className="text-orange-600">Shark</span></span>
                </div>
              </div>
              <p className="text-sm">
                Empowering car buyers with insider tactics to save thousands.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-orange-500 transition-colors">Free Calendar</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">eBook</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Consultation</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-orange-500 transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Success Stories</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-orange-500 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2025 AutoShark. All rights reserved. Never overpay again.</p>
          </div>
        </div>
      </footer>

      {/* Consultation Popup */}
      <Dialog open={showConsultationPopup} onOpenChange={setShowConsultationPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              🚀 Don't Overpay on Your Next Car!
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Book a 1-on-1 consultation with our experts and save thousands
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center mb-2">
                <Phone className="w-5 h-5 text-orange-600 mr-2" />
                <span className="font-semibold text-gray-900">Quick Call - $14.99</span>
              </div>
              <p className="text-sm text-gray-600">15 minutes of expert advice</p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-4 rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Strategy Session - $59.99</span>
                </div>
                <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded">POPULAR</span>
              </div>
              <p className="text-sm text-white/90">45 minutes • Save thousands with insider tactics</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center mb-2">
                <Award className="w-5 h-5 text-orange-600 mr-2" />
                <span className="font-semibold text-gray-900">Full Support - $249.99</span>
              </div>
              <p className="text-sm text-gray-600">Complete hand-holding + in-person assistance</p>
            </div>

            <Button 
              onClick={() => {
                setShowConsultationPopup(false);
                navigate('/consultation');
              }}
              className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-lg"
            >
              View All Packages <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <button
              onClick={() => setShowConsultationPopup(false)}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* eBook Popup */}
      <Dialog open={showEbookPopup} onOpenChange={setShowEbookPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              📚 Get The Dealership Killer eBook
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Every negotiation tactic dealerships don't want you to know
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-6 rounded-lg text-white text-center">
              <div className="text-4xl font-bold mb-2">$24.99</div>
              <div className="text-white/90">One-time payment • Lifetime access</div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Word-for-word negotiation scripts</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">How to defend against F&I upsells</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Trade-in value maximization tactics</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Hidden fee elimination techniques</span>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium">
                ⚡ Limited Time: Our customers save $4,200+ on average
              </p>
            </div>

            <Button 
              onClick={() => {
                setShowEbookPopup(false);
                handleEbookAccess();
              }}
              className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-lg"
            >
              <BookOpen className="mr-2 w-5 h-5" />
              Get Instant Access Now
            </Button>
            
            <button
              onClick={() => setShowEbookPopup(false)}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              No thanks, I'll keep overpaying
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

};

export default LandingPage;